<ng-container *transloco="let t">
  <ng-template #fileContentTemplate>
    <i
      [class]="
        'pi pi-file ' +
        (!isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)
          ? 'text-gray-600 dark:text-gray-300'
          : 'text-gray-400')
      "
    ></i>
    <div class="flex flex-col grow">
      <span
        [class]="
          'font-medium text-left ' +
          (!isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)
            ? 'text-gray-600 dark:text-gray-300'
            : 'text-gray-400')
        "
        >{{ file.fileName }}</span
      >
      <span
        [class]="
          'text-sm text-start ' +
          (!isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)
            ? 'text-gray-600 dark:text-gray-300'
            : 'text-gray-400')
        "
        >{{ file.fileSizeBytes | fileSize }}</span
      >
      <!-- Expiry message -->
      @if (file.expiryDate) {
        <p class="text-yellow-600 dark:text-yellow-400 text-sm self-start">
          @if (file.fileUploadState === FileUploadStateEnum.Deleted) {
            {{
              t("app.formly-types.fileDownloadDisplay.expiredMessage", {
                expiryDate: file.expiryDate | ranDate: "dateLong",
              })
            }}
          } @else {
            {{
              t("app.formly-types.fileDownloadDisplay.expiryMessage", {
                expiryDate: file.expiryDate | ranDate: "dateLong",
              })
            }}
          }
        </p>
      }
    </div>
    <!-- File upload failed, or file deleted error message -->
    @if (isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)) {
      <div class="flex flex-row items-center gap-2">
        <i class="pi pi-times-circle text-red-500 dark:text-red-400"></i>
        <span class="text-red-500 dark:text-red-400">{{ t("app.transactionAttachment.fileUnavailable") }}</span>
      </div>
    }
  </ng-template>

  <ng-template #trashButton>
    @if (canDelete && !isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)) {
      <p-button
        class="animate-all duration-150 h-full"
        styleClass="h-full"
        icon="pi pi-trash"
        severity="danger"
        [text]="true"
        [loading]="isDeleting"
        [disabled]="isDeleting"
        (click)="confirmDelete()"
        [pTooltip]="t('app.transactionAttachment.deleteFileTooltip')"
        tooltipPosition="top"
      ></p-button>
    }
  </ng-template>

  <!-- User-deleted file: static audit row -->
  @if (isDeletedByUser) {
    <div
      class="grow flex items-center gap-3 mb-2 last:mb-0 p-3 border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 rounded"
    >
      <i class="pi pi-trash text-gray-400"></i>
      <div class="flex flex-col grow">
        <span class="font-medium text-gray-400 line-through">{{ file.fileName }}</span>
        @if (file.deletedAt && file.deletedByUser) {
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{
              t("app.transactionAttachment.deletedMessage", {
                user: file.deletedByUser.fullName,
                date: file.deletedAt | ranDate: "long",
              })
            }}
          </span>
        }
      </div>
    </div>
    <!-- PDF-specific layout -->
  } @else if (file.fileType === FileTypeEnum.Pdf) {
    <div class="grow flex items-stretch gap-2 mb-2 last:mb-0">
      <div class="grow grid grid-cols-[9fr_1px_1fr] gap-0">
        <!-- Left column -->
        <p-button
          [class]="
            'flex animate-all duration-150 gray-button expand no-right-radius ' +
            (isFailedUploadOrDeleted(file.createdAt, file.fileUploadState) ? 'col-span-3' : '')
          "
          (click)="previewFile()"
          [loading]="isPreviewing"
          [disabled]="
            isFailedUploadOrDeleted(file.createdAt, file.fileUploadState) || isDownloading || isPreviewing || isDeleting
          "
          [pTooltip]="t('app.transactionAttachment.previewFileTooltip')"
          tooltipPosition="top"
          [tooltipDisabled]="isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)"
        >
          <ng-container *ngTemplateOutlet="fileContentTemplate" />
        </p-button>
        @if (!isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)) {
          <!-- Divider -->
          <p-divider
            class="[--p-divider-vertical-margin:0] [--p-divider-border-color:var(--color-gray-300)] dark:[--p-divider-border-color:var(--color-gray-600)]"
            type="solid"
            layout="vertical"
          ></p-divider>
          <!-- Download button -->
          <p-button
            class="animate-all duration-150 gray-button expand no-left-radius"
            (click)="downloadFile()"
            [loading]="isDownloading"
            [disabled]="
              isFailedUploadOrDeleted(file.createdAt, file.fileUploadState) ||
              isDownloading ||
              isPreviewing ||
              isDeleting
            "
            icon="pi pi-download text-gray-600 dark:text-gray-300"
            [pTooltip]="t('app.transactionAttachment.downloadFileTooltip')"
            tooltipPosition="top"
            [tooltipDisabled]="isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)"
          >
          </p-button>
        }
      </div>
      <ng-container *ngTemplateOutlet="trashButton" />
    </div>
    <!-- Catch-all layout for everything else -->
  } @else {
    <div class="grow flex items-stretch gap-2 mb-2 last:mb-0">
      <p-button
        class="grow flex animate-all duration-150 gray-button expand"
        (click)="downloadFile()"
        [loading]="isDownloading"
        [disabled]="isFailedUploadOrDeleted(file.createdAt, file.fileUploadState) || isDownloading || isDeleting"
        [pTooltip]="t('app.transactionAttachment.downloadFileTooltip')"
        tooltipPosition="top"
        [tooltipDisabled]="isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)"
      >
        <div class="grow flex items-center gap-2">
          <!-- Left column -->
          <ng-container *ngTemplateOutlet="fileContentTemplate" />
          <!-- Right column -->
          @if (!isFailedUploadOrDeleted(file.createdAt, file.fileUploadState)) {
            <!-- Download icon -->
            <div class="flex flex-row items-center mr-6" [hidden]="isDownloading">
              <i class="pi pi-download text-gray-600 dark:text-gray-300"></i>
            </div>
          }
        </div>
      </p-button>
      <ng-container *ngTemplateOutlet="trashButton" />
    </div>
  }
</ng-container>
